import clsx from 'clsx';
import { isEmpty, omit } from 'lodash';
import { func, object, oneOf, string } from 'prop-types';
import { createContext, useContext, useEffect } from 'react';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';

import { Card, FormCheckbox, FormInput, RadioGroup } from 'components';
import { noOnlySpacesRegex, postalCodeRegex } from 'constants';
import { processString } from 'helpers';
import { useCivility, useFetch, useSafeState, useUser } from 'hooks';

const AddressContext = createContext();

const ActionButtons = ({ className = '' }) => {
  const { t } = useTranslation();
  const { address, addressType, isEditable, setIsEditable, setFieldData } = useContext(AddressContext);

  return (
    <>
      {(addressType === 'Domicile' || (addressType === 'Livraison' && !address?.sameAddress)) && (
        <div className={clsx('flex justify-end items-center gap-4', className)}>
          {isEditable && (
            <>
              <button className="btn small" type="submit">
                {t('buttons.save')}
              </button>

              <button
                type="button"
                className="btn outlined small"
                onClick={() => {
                  setIsEditable(false);
                  setFieldData();
                }}
              >
                {t('buttons.cancel')}
              </button>
            </>
          )}

          {!isEditable && (
            <button
              type="button"
              className="btn btn-primary small"
              onClick={() => {
                setIsEditable(true);
              }}
            >
              {t('buttons.modify')}
            </button>
          )}
        </div>
      )}
    </>
  );
};

const AddressForm = ({ address, title, addressType, copyAddress }) => {
  const { currentUser } = useUser();
  const { t } = useTranslation();
  const { setValue, handleSubmit, clearErrors } = useFormContext();

  const [isEditable, setIsEditable] = useSafeState(false);
  const { call: userAddressUpdateCall, loading: userAddressUpdateLoading } = useFetch();
  const { civilityOptions } = useCivility(`civility${addressType}`);

  useEffect(() => {
    setFieldData();
  }, [address, addressType]);

  const setFieldData = () => {
    if (isEmpty(address)) return;

    for (const [key, value] of Object.entries(address)) {
      setValue(key, value);
    }

    setValue(
      `civility${addressType}`,
      address.civility === 'Mme' ? civilityOptions[1].value : civilityOptions[0].value
    );

    clearErrors();
  };

  const onSubmit = (newUserAddress) => {
    // Remove firstname & lastname on domicile address
    if (addressType === 'Domicile') {
      newUserAddress = omit(newUserAddress, 'firstname');
      newUserAddress = omit(newUserAddress, 'name');
    }

    // Change civility key for livraison address && add addressType before call
    if (addressType === 'Livraison') newUserAddress.civility = newUserAddress[`civility${addressType}`];
    if (addressType === 'Livraison') {
      newUserAddress.firstname = processString(newUserAddress.firstName);
      newUserAddress.name = processString(newUserAddress.name);
    }
    newUserAddress = omit(newUserAddress, `civility${addressType}`);
    newUserAddress = omit(newUserAddress, 'firstName');
    newUserAddress.type = addressType;

    setIsEditable(false);
    updateUserAddresses(newUserAddress);
  };

  const updateUserAddresses = async (newUserAddress) => {
    if (!currentUser?.serialNumber || userAddressUpdateLoading) return;

    const { data } =
      (await userAddressUpdateCall({
        service: 'castelis',
        path: `/users/addresses/${currentUser.serialNumber}`,
        method: 'put',
        // TODO : Check if headers can be removed when API is fixed
        headers: {
          'Content-Type': null
        },
        query: newUserAddress
      })) || {};

    if (data?.status === 200 && newUserAddress.sameAddress) copyAddress();
    data?.status === 200 && toast.success(t('dashboard:toast.success'));
  };

  return (
    <Card onMobile={false} className="h-full my-6 lg:my-0">
      <AddressContext.Provider value={{ address, addressType, isEditable, setIsEditable, setFieldData }}>
        <form onSubmit={handleSubmit(onSubmit)} className="address-form">
          <div className="flex justify-between w-full mb-6">
            <h2 className="h2 underlined-yellow">{title}</h2>
            <ActionButtons className="hidden lg:flex" />
          </div>

          <RadioGroup
            radioOptions={civilityOptions}
            groupLabel={t('form:civility.label')}
            row
            className="grid grid-cols-12 w-full gap-4 items-center mb-5"
            disabled={addressType === 'Domicile' ? true : !isEditable}
          />

          <div className="grid grid-cols-12 w-full gap-4 mb-5 user-name-container">
            <p className={clsx('label col-span-4', !isEditable && 'disabled')}>{t('form:fullname')}</p>
            <FormInput
              name="name"
              disabled={addressType === 'Domicile' ? true : !isEditable}
              className="col-span-4"
              placeholder={t('form:lastname')}
              rules={{
                required: addressType === 'Livraison',
                pattern: {
                  value: noOnlySpacesRegex,
                  message: t('noOnlySpaces')
                }
              }}
              onBlur={(value) => setValue('name', processString(value), { shouldValidate: true })}
            />
            <FormInput
              name="firstName"
              disabled={addressType === 'Domicile' ? true : !isEditable}
              className="col-span-4"
              placeholder={t('form:firstname')}
              rules={{
                required: addressType === 'Livraison',
                pattern: {
                  value: noOnlySpacesRegex,
                  message: t('noOnlySpaces')
                }
              }}
              onBlur={(value) => setValue('firstName', processString(value), { shouldValidate: true })}
            />
          </div>

          <div className="grid grid-cols-12 w-full gap-4 items-center mb-5">
            <p className={clsx('label col-span-4', !isEditable && 'disabled')}>{t('form:address')}</p>
            <FormInput
              name="address1"
              rules={{
                required: true,
                pattern: {
                  value: noOnlySpacesRegex,
                  message: t('noOnlySpaces')
                }
              }}
              disabled={!isEditable}
              className="col-span-8"
            />
          </div>

          <div className="grid grid-cols-12 w-full gap-4 items-center mb-5">
            <p className={clsx('label col-span-4', !isEditable && 'disabled')}>{t('form:addressComplementary')}</p>
            <FormInput
              name="address2"
              rules={{
                pattern: {
                  value: noOnlySpacesRegex,
                  message: t('noOnlySpaces')
                }
              }}
              disabled={!isEditable}
              className="col-span-8"
            />
          </div>

          <div className="grid grid-cols-12 w-full gap-4 items-center mb-5">
            <p className={clsx('label col-span-4', !isEditable && 'disabled')}>{t('form:addressComplementary1')}</p>
            <FormInput
              name="address3"
              rules={{
                pattern: {
                  value: noOnlySpacesRegex,
                  message: t('noOnlySpaces')
                }
              }}
              disabled={!isEditable}
              className="col-span-8"
            />
          </div>

          <div className="grid grid-cols-12 w-full gap-4 mb-5 address-container">
            <p className={clsx('label col-span-4', !isEditable && 'disabled')}>{t('form:cityPostalCode')}</p>
            <FormInput
              name="postalCode"
              disabled={!isEditable}
              className="col-span-4"
              placeholder="75000"
              rules={{
                required: true,
                pattern: {
                  value: postalCodeRegex,
                  message: t('postalCodeInvalid')
                }
              }}
            />
            <FormInput
              name="city"
              disabled={!isEditable}
              className="col-span-4"
              placeholder="Paris"
              rules={{
                required: true,
                pattern: {
                  value: noOnlySpacesRegex,
                  message: t('noOnlySpaces')
                }
              }}
            />
          </div>

          {addressType === 'Domicile' && (
            <FormCheckbox
              name="sameAddress"
              labelI18nKey="dashboard:sameAddress"
              disabled={!isEditable}
              className="mb-5"
            />
          )}

          <ActionButtons className="lg:hidden" />
        </form>
      </AddressContext.Provider>
    </Card>
  );
};

AddressForm.propTypes = {
  address: object,
  addressType: oneOf(['Livraison', 'Domicile']),
  copyAddress: func,
  title: string
};

ActionButtons.propTypes = {
  className: string
};

export default AddressForm;
